<template>
	<div class="person-panel-list" ref="$personPanelList">
		<app-loader v-if="isLoadingAll" :style="loaderStyle"/>
		<div
			v-if="isEmpty"
			class="person-panel-list__empty"
		>
			{{ $t('contacts_empty') }}
		</div>
		<template v-else>
			<div class="person-panel-list__count">
				{{ $t('contacts_found') }}:
				<div class="person-panel-list__count-number">{{ clients?.Count || 0 }}</div>
			</div>
			<div
				v-for="group in clients?.Groups"
				class="person-panel-list__group"
				:key="group.Id"
			>
				<div class="person-panel-list__group-name">
					{{ group.Name }}:
				</div>
				<template
					v-for="contact in group.Persons"
					:key="contact.Id"
				>
					<clients-panel-list-item
						:client="contact"
						:search-text="props.searchText"
					/>
					<div class="person-panel-list__divider"/>
				</template>
			</div>
			<div
				v-if="!isLoadingAll && isLoadingPartial"
				class="person-panel-list__partial"
			>
				<app-loader :style="partialLoaderStyle" />
			</div>
			<div
				v-else-if="!isLoadingAll && !canLoadNext"
				class="person-panel-list__end"
			>
				{{ $t('contacts_all_loaded') }}
			</div>
		</template>
		<app-scroll-top-button
			:enabled="isScrollTopEnabled"
			@on-click="scrollToTop"
		/>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref, watch
} from "vue";
import {useRoute} from "vue-router";
import ClientsPanelListItem from "@/components/main/clients-panel/ClientsPanelListItem.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppScrollTopButton from "@/components/ui-kit/button/AppScrollTopButton.vue";
import { useScrollTopButton } from "@/composables/useScrollTopButton";
import {clientPageStore} from "@/store/client-page";

const props = defineProps<{
	searchText: string;
}>()

const emit = defineEmits<{
	(e: 'on-get-partial'): void
}>()

const clientStore = clientPageStore()
const route = useRoute()
const $personPanelList = ref<HTMLDivElement | null>(null)

const HIGHLIGHT_CLASS = 'client-item--highlight'

const parseContactId = (raw: unknown): number | null => {
	if (raw == null || Array.isArray(raw) || raw === '') {
		return null
	}
	const id = Number(raw)
	return Number.isFinite(id) && id > 0 ? id : null
}

const pendingContactId = ref<number | null>(parseContactId(route.query.contactId))

const isLoadingAll = computed(() => clientStore.clientContactsLoading);
const isLoadingPartial = computed(() => clientStore.clientContactsPartialLoading);
const canLoadNext = computed(() => clientStore.canLoadNextContacts);
const clients = computed(() => clientStore.clientContacts);

const loadedCount = computed(() => {
	const groups = (clients.value as unknown as { Groups?: { Persons?: unknown[] }[] } | null)?.Groups ?? []
	return groups.reduce((acc, group) => acc + (group.Persons?.length ?? 0), 0)
});

const isEmpty = computed(() => !isLoadingAll.value && loadedCount.value === 0);

const { isScrollTopEnabled, scrollToTop } = useScrollTopButton(
	$personPanelList,
	'[data-contact-id]',
	() => clients.value
)

const highlightContact = async (id: number) => {
	await nextTick()

	const container = $personPanelList.value
	const el = container?.querySelector<HTMLElement>(`[data-contact-id="${id}"]`)
	if (!container || !el) {
		return
	}

	const containerRect = container.getBoundingClientRect()
	const elRect = el.getBoundingClientRect()
	const target = container.scrollTop
		+ (elRect.top - containerRect.top)
		- (container.clientHeight - elRect.height) / 2

	container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' })

	el.classList.add(HIGHLIGHT_CLASS)
	const onEnd = (event: AnimationEvent) => {
		if (event.target !== el) return
		el.classList.remove(HIGHLIGHT_CLASS)
		el.removeEventListener('animationend', onEnd)
	}
	el.addEventListener('animationend', onEnd)
}

const onScroll = () => {
	const el = $personPanelList.value
	if (!el) return

	if (
		!clientStore.clientContactsLoading
		&& !clientStore.clientContactsPartialLoading
		&& clientStore.canLoadNextContacts
		&& el.scrollTop + el.offsetHeight > el.scrollHeight - 1000
	) {
		emit('on-get-partial')
	}
}

onMounted(() => {
	$personPanelList.value?.addEventListener('scroll', onScroll)
})

onBeforeUnmount(() => {
	$personPanelList.value?.removeEventListener('scroll', onScroll)
})

watch(isLoadingAll, async (loading, wasLoading) => {
	if (loading) {
		$personPanelList.value!.scrollTop = 0
		return
	}
	if (!wasLoading) {
		return
	}

	const id = pendingContactId.value
	pendingContactId.value = null
	if (id != null) {
		await highlightContact(id)
	}
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const partialLoaderStyle = computed(() => ({
	position: 'relative',
	height: '32px',
	'background-color': 'transparent'
}))

</script>

<style scoped lang="scss">
.person-panel-list {
	display: flex;
	flex-direction: column;
	flex: 1;
	position: relative;
	border-bottom-left-radius: 8px;
	border-bottom-right-radius: 8px;
	overflow: auto;
	background-color: #fff;

	&__count {
		display: flex;
		align-items: center;
		justify-content: center;
		margin-top: 4px;
		font-size: 12px;
		line-height: 18px;
		color: #4D4D4D;
		&-number {
			font-weight: 600;
			margin-left: 4px;
		}
	}

	&__empty {
		margin: auto;
		padding: 16px;
		text-align: center;
		font-size: 12px;
		line-height: 16px;
		color: #4D4D4D;
	}

	&__divider {
		flex-shrink: 0;
		height: 2px;
		margin-left: 8px;
		border-radius: 1px;
		background-color: #F2F2F2;
	}

	&__end {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		padding: 8px 0;
		font-size: 12px;
		line-height: 16px;
		color: #4D4D4D;
	}

	&__partial {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		padding: 4px 0 8px;
	}

	&__group {
		display: flex;
		flex-direction: column;
	}

	&__group-name {
		font-size: 12px;
		line-height: 16px;
		font-weight: 500;
		color: #999999;
		padding: 4px 8px;
	}
}
</style>
